// Conversational authoring §3 — the per-section interrogation scripts: the OWNER'S AUTHORING SURFACE.
// One script per taxonomy section, walked in the render's OUTPUT_SECTIONS blueprint order (the walk itself teaches
// the Canadian CV structure). Each script says what a complete entry CONTAINS (standard), asks the questions that
// ELICIT each part (the user writes every word), and — for IMG-stream applicants — carries the hedged presence
// prompt for that section ("is it on your CV?", recover-framed, never "go acquire it").
//
// GROUNDING: the standard/exemplar lines mirror workers/src/lib/sectionStandard.ts (U Sask MD CV Guide 2025,
// U Alberta OAW CaRMS prep, CaRMS custom-CV guidance; CanMEDS naming per the owner ruling there). Keep the two
// aligned when refining. FIREWALL: everything here is a GENERIC CONSTANT — questions and placeholder shapes only;
// no user fact is ever interpolated into CV text; nothing composes.
//
// Drafted by CC on the owner's delegation (2026-07-07); the owner refines wording here, no engine change needed.

export interface FieldQ {
  field: 'title' | 'organization' | 'dateText';
  q: string;
  help: string;
  optional?: boolean;
}

export interface SectionScript {
  id: string;            // taxonomy section id (matches /api/sections)
  intro: string;         // the consultant's framing when the section opens
  standard: string;      // what a complete entry here contains — shown as quiet guidance, with placeholders
  fields: FieldQ[];      // asked ONE at a time
  bulletQ: string;       // the write-a-line prompt (experience: the loop; credential: optional detail)
  bulletHelp: string;    // guidance under the prompt — elicits, never writes
  askAnother: string;    // after a save
  imgNote?: string;      // Phase-4 weave: hedged presence prompt, shown in the intro when stream === 'img'
}

// Blueprint order — flattened from the render's OUTPUT_SECTIONS grouping (education first, additional last).
export const GUIDED_ORDER: string[] = [
  'education', 'postgraduate-training',
  'clinical-experience',
  'research-and-scholarly-activity', 'publications', 'presentations',
  'teaching-experience',
  'leadership-and-administrative', 'volunteer-and-community-involvement',
  'awards-and-honours',
  'work-employment',
  'medical-licensure', 'certifications', 'examinations', 'professional-development-cpd', 'professional-memberships',
  'extracurricular-activities-and-interests',
  'languages', 'additional-information',
];

const WHEN: FieldQ = {
  field: 'dateText', q: 'When was this — roughly?',
  help: 'Any honest form works: "2019 – 2021", "summer 2023", "2020 / present".',
};

export const GUIDED_SCRIPTS: Record<string, SectionScript> = {
  'education': {
    id: 'education',
    intro: 'Education first — a Canadian residency CV usually opens with it. Degrees, medical school, and any other formal programs, most recent first.',
    standard: 'A complete education entry names the degree or program, the institution, and the years.',
    fields: [
      { field: 'title', q: 'What degree or program is this?', help: 'e.g. the degree name as it appears on the credential — "MD", "MBBS", "BSc (Hons), Biology".' },
      { field: 'organization', q: 'Which institution granted it?', help: 'The university or school, and the city/country if it helps place it.' },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'A distinction, thesis topic, or standing — only if it’s true and adds something. Many education entries need no line at all.',
    askAnother: 'Add another degree or program, or move on?',
  },
  'postgraduate-training': {
    id: 'postgraduate-training',
    intro: 'Any postgraduate training — internships, residencies elsewhere, fellowships, house jobs. For many IMGs this is where substantial clinical depth lives.',
    standard: 'A complete training entry shows the role and program, the institution, the period, and what your responsibilities actually were.',
    fields: [
      { field: 'title', q: 'What was the training role?', help: 'e.g. "Rotating intern", "Resident, Internal Medicine", "House officer".' },
      { field: 'organization', q: 'Where — which hospital or program?', help: 'Institution and city/country.' },
      WHEN,
    ],
    bulletQ: 'What did you actually do there — write one line in your own words.',
    bulletHelp: 'A reviewer wants your level of responsibility and what you handled: the wards, the volumes, the procedures — whatever is true for you.',
    askAnother: 'Add another training post, or move on?',
  },
  'clinical-experience': {
    id: 'clinical-experience',
    intro: 'Now the heart of it — clinical experience. Rotations, clerkships, observerships, clinical work. One entry per placement or role.',
    // OUTCOMES ARE NEVER REQUIRED (2026-08-09, same correctness fix as workers/src/lib/sectionStandard.ts). The old
    // copy ended "…the setting, and what changed", making an outcome a required element — contradicting the engine's
    // hard rule that a bullet is never flagged solely for lacking one. Kept in this surface's own second-person voice.
    standard: 'A strong clinical entry shows your Medical Expert role — your scope, your level of responsibility (student / observer / resident), the clinical context, and your contribution. Add an outcome only if it was genuinely observed or measured. Shape: "As [level], managed [what] for [N] patients in [setting]."',
    fields: [
      { field: 'title', q: 'What was your role in this placement?', help: 'Name the role AND your level of responsibility — "Clinical clerk (student)", "Observer, Cardiology", "Resident". Reviewers read the level first.' },
      { field: 'organization', q: 'Where was it — which hospital or clinic?', help: 'The institution, and the city if it helps place it.' },
      WHEN,
    ],
    bulletQ: 'Describe what you did — one line, your own words.',
    // Same fix: the old help text ("what changed because you were there… What was different after your involvement?")
    // both REQUIRED an outcome and PRESUPPOSED that something changed. It now asks for the contribution.
    bulletHelp: 'Say what you handled and what you personally did with it — which patients or cases, how much responsibility you carried, what you decided. Only what you actually did.',
    askAnother: 'Add another placement, or move on?',
    imgNote: 'IMG-stream note: programs often look for Canadian clinical exposure — an observership, elective, or clinical assistantship here. If you’ve done any, this is the section for it. Is it on your CV?',
  },
  'research-and-scholarly-activity': {
    id: 'research-and-scholarly-activity',
    intro: 'Research and scholarly activity — projects you worked on, whether or not they published. Publications themselves come next; this is the work.',
    standard: 'A strong research entry demonstrates the Scholar role — the study, YOUR specific role, the method, the scope, and what it showed. Shape: "Designed and ran [method] for a [N]-participant study of [topic]; [what it showed]."',
    fields: [
      { field: 'title', q: 'What was the project?', help: 'The study or project in a phrase — topic and design if you can.' },
      { field: 'organization', q: 'Where, or with whom?', help: 'The lab, department, or supervisor’s group. Skip if it’s covered by the project name.', optional: true },
      WHEN,
    ],
    bulletQ: 'What was YOUR part in it — one line, your own words.',
    bulletHelp: 'Your specific role and contribution: what you designed, collected, analyzed, or wrote — and what the work showed. "Assisted with research" hides you; say what you did.',
    askAnother: 'Add another project, or move on?',
  },
  'publications': {
    id: 'publications',
    intro: 'Publications — papers, abstracts, chapters. These are citations, so exactness matters more than style.',
    standard: 'A complete publication entry is an honest citation: authors (your position visible), title, venue, year, and status if not yet in print ("submitted" and "published" are different claims).',
    fields: [
      { field: 'title', q: 'Give the citation, as exactly as you can.', help: 'Authors, title, journal/venue, year. Keep your author position as it truly is.' },
      { field: 'organization', q: 'Status, if not yet published?', help: 'e.g. "in press", "submitted", "preprint". Skip if published.', optional: true },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'Rarely needed — the citation usually carries it.',
    askAnother: 'Add another publication, or move on?',
  },
  'presentations': {
    id: 'presentations',
    intro: 'Presentations — posters and talks, at conferences, rounds, or meetings.',
    standard: 'A complete presentation entry names the work, the format (poster / oral), the meeting, and the year — a presentation and a publication are different claims, so keep them apart.',
    fields: [
      { field: 'title', q: 'What did you present, and in what format?', help: 'e.g. "[Title] — poster" or "[Title] — oral presentation".' },
      { field: 'organization', q: 'At which meeting or venue?', help: 'The conference, rounds, or event name.' },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'e.g. an award for the poster — only if true.',
    askAnother: 'Add another presentation, or move on?',
  },
  'teaching-experience': {
    id: 'teaching-experience',
    intro: 'Teaching — formal or informal. If you supervised, onboarded, or mentored anyone junior to you, that counts as teaching; many applicants under-claim here.',
    standard: 'A strong teaching entry demonstrates the Scholar role — who you taught, the format, the scope (how many / how often), and what they took away. Shape: "Taught [audience] through [format] across [N sessions]; [what they took away]."',
    fields: [
      { field: 'title', q: 'What was the teaching role?', help: 'e.g. "Peer tutor", "Bedside teaching — junior clerks", "Exam-prep instructor".' },
      { field: 'organization', q: 'Where, or for whom?', help: 'The institution, program, or group.', optional: true },
      WHEN,
    ],
    bulletQ: 'Describe the teaching — one line, your own words.',
    bulletHelp: 'Who did you teach, how often, and what did they take away — feedback or a result if you have one.',
    askAnother: 'Add another teaching role, or move on?',
  },
  'leadership-and-administrative': {
    id: 'leadership-and-administrative',
    intro: 'Leadership and administrative roles — committees, organizing, coordinating, anything you ran or helped run.',
    standard: 'A strong entry demonstrates the Leader role — what you led, the scale, and what changed. Shape: "Led [initiative] for [N people / scope]; [what changed]."',
    fields: [
      { field: 'title', q: 'What was the role?', help: 'e.g. "Class representative", "Conference organizing committee", "Clinic scheduling lead".' },
      { field: 'organization', q: 'In which organization or context?', help: 'The body, event, or institution.', optional: true },
      WHEN,
    ],
    bulletQ: 'What did you lead or organize — one line, your own words.',
    bulletHelp: 'The scale (how many people, how big) and what changed because you ran it.',
    askAnother: 'Add another role, or move on?',
  },
  'volunteer-and-community-involvement': {
    id: 'volunteer-and-community-involvement',
    intro: 'Volunteer and community work — service outside the job. If you ran a clinic day, organized a drive, or served a community, it belongs here.',
    standard: 'A strong entry here shows Health Advocate territory — what you did, for whom, the scale, and what changed for them.',
    fields: [
      { field: 'title', q: 'What was the volunteer role?', help: 'e.g. "Volunteer, community health screening", "Crisis-line responder".' },
      { field: 'organization', q: 'With which organization or community?', help: 'The group or community served.', optional: true },
      WHEN,
    ],
    bulletQ: 'Describe the work — one line, your own words.',
    bulletHelp: 'Who it served, how often, and what changed. Sustained service reads more strongly than one-offs — say the duration if it’s true.',
    askAnother: 'Add another, or move on?',
  },
  'awards-and-honours': {
    id: 'awards-and-honours',
    intro: 'Awards and honours — scholarships, prizes, distinctions.',
    standard: 'A complete award entry names the award, the granting body, and the year. If it’s competitive, the scope ("1 of N") is worth stating — only if you know it.',
    fields: [
      { field: 'title', q: 'What’s the award called?', help: 'Its formal name.' },
      { field: 'organization', q: 'Who granted it?', help: 'The institution or body.' },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'e.g. what it recognizes, or its selectivity — only if true and known.',
    askAnother: 'Add another award, or move on?',
  },
  'work-employment': {
    id: 'work-employment',
    intro: 'Work experience — paid roles, clinical or not. Non-medical work still shows real skills; don’t leave it off just because it wasn’t in a hospital.',
    standard: 'A strong work entry foregrounds what you did — your role, the scope, and the outcome or evidence of your contribution.',
    fields: [
      { field: 'title', q: 'What was the job?', help: 'The role title.' },
      { field: 'organization', q: 'Where?', help: 'Employer and setting.' },
      WHEN,
    ],
    bulletQ: 'What did you do there — one line, your own words.',
    bulletHelp: 'What you handled or improved, and any evidence — scope, feedback, a result.',
    askAnother: 'Add another job, or move on?',
  },
  'medical-licensure': {
    id: 'medical-licensure',
    intro: 'Medical licensure — any license or registration you hold.',
    standard: 'A complete licensure entry names the license/registration, the issuing body, and the year (and status, if provisional).',
    fields: [
      { field: 'title', q: 'What license or registration?', help: 'As it’s formally named, with status if relevant.' },
      { field: 'organization', q: 'Issued by which body?', help: 'The college or authority.' },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'Rarely needed.',
    askAnother: 'Add another, or move on?',
  },
  'certifications': {
    id: 'certifications',
    intro: 'Certifications — ACLS, BLS, ATLS, and the like.',
    standard: 'A complete certification entry names the certificate, the issuer, and the year (note expiry only if current).',
    fields: [
      { field: 'title', q: 'Which certification?', help: 'e.g. "ACLS", "BLS provider".' },
      { field: 'organization', q: 'Issued by?', help: 'The certifying body.', optional: true },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'Rarely needed.',
    askAnother: 'Add another certification, or move on?',
  },
  'examinations': {
    id: 'examinations',
    intro: 'Examinations — licensing and qualifying exams you’ve taken.',
    standard: 'A complete exam entry names the exam and the year taken (result only if you choose to state it — accuracy over impression).',
    fields: [
      { field: 'title', q: 'Which exam?', help: 'e.g. "MCCQE Part 1", "NAC-OSCE". The exact name matters.' },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'Only what’s true — a standing or result you want on record.',
    askAnother: 'Add another exam, or move on?',
    imgNote: 'IMG-stream note: programs commonly expect MCCQE Part 1 — and many look for NAC-OSCE (requirements vary by cycle and program). If you’ve taken them, they belong here. Are they on your CV?',
  },
  'professional-development-cpd': {
    id: 'professional-development-cpd',
    intro: 'Professional development — courses, workshops, CPD activities.',
    standard: 'A complete entry names the course or activity, the provider, and the year.',
    fields: [
      { field: 'title', q: 'What was the course or activity?', help: 'Its name in a phrase.' },
      { field: 'organization', q: 'Run by whom?', help: 'The provider.', optional: true },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'Rarely needed.',
    askAnother: 'Add another, or move on?',
  },
  'professional-memberships': {
    id: 'professional-memberships',
    intro: 'Professional memberships — associations and societies you belong to.',
    standard: 'A complete membership entry names the association and the period of membership.',
    fields: [
      { field: 'title', q: 'Which association?', help: 'e.g. a national student or specialty association.' },
      WHEN,
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'e.g. a role you held in it — though a real role may belong under Leadership instead.',
    askAnother: 'Add another membership, or move on?',
  },
  'extracurricular-activities-and-interests': {
    id: 'extracurricular-activities-and-interests',
    intro: 'Interests and activities — the person behind the file. Programs do read this section; keep it honest and specific.',
    standard: 'A good interests entry is specific ("distance running — three half-marathons" reads better than "sports") and true.',
    fields: [
      { field: 'title', q: 'What’s the activity or interest?', help: 'Specific beats general.' },
      { field: 'dateText', q: 'Since when, or how long? (optional)', help: 'Skip if it doesn’t fit.', optional: true },
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'A concrete marker — a level reached, something you made or completed.',
    askAnother: 'Add another, or move on?',
  },
  'languages': {
    id: 'languages',
    intro: 'Languages — what you speak, and how well.',
    standard: 'A complete language entry names the language and your honest working level; a formal test score (with the year) is the strongest form.',
    fields: [
      { field: 'title', q: 'Which language, and at what level?', help: 'e.g. "English — fluent (IELTS 8.0, 2024)", "French — conversational".' },
    ],
    bulletQ: 'Anything worth a line under it? (optional)',
    bulletHelp: 'Rarely needed.',
    askAnother: 'Add another language, or move on?',
    imgNote: 'IMG-stream note: many programs ask for evidence of language proficiency (IELTS / CELPIP or equivalent; requirements vary). If you’ve taken a test, the score and year belong here. Is it on your CV?',
  },
  'additional-information': {
    id: 'additional-information',
    intro: 'Last section — anything that matters and didn’t fit. Career gaps explained plainly, unusual paths, anything a reviewer should hear from you rather than guess.',
    standard: 'A good entry here is plain and factual — reviewers respect a direct explanation.',
    fields: [
      { field: 'title', q: 'Give it a short heading.', help: 'e.g. "Career note", "Relocation".' },
      { field: 'dateText', q: 'When does it refer to? (optional)', help: 'Skip if it doesn’t fit.', optional: true },
    ],
    bulletQ: 'Say it in your own words — one line.',
    bulletHelp: 'Plain and factual. Your words, your framing.',
    askAnother: 'Add another note, or move on?',
  },
};
